import React, { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import AuthLoading from './views/AuthLoading';
import { Provider } from "react-redux";
import ProtectedRoute from './views/ProtectedRoute';
import { FirebaseProvider, store } from "common";
import { FirebaseConfig } from './config/FirebaseConfig';
import { GoogleMapApiConfig } from './config/GoogleMapApiConfig';
import { GoogleMapApiOptions } from './mapConfig';
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { ThemeProvider } from '@mui/styles';
import { createTheme, CircularProgress, Box } from '@mui/material';
import { useJsApiLoader } from '@react-google-maps/api';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { webClientId } from "config/ClientIds.js";
import { HelmetProvider } from "react-helmet-async";

// ── Lazy-loaded views (code splitting — cada ruta carga su chunk propio) ──────
const LandingPage        = lazy(() => import('./views/LandingPage'));
const LoginPage          = lazy(() => import('./views/LoginPage'));
const PrivacyPolicy      = lazy(() => import('./views/PrivacyPolicy'));
const AboutUs            = lazy(() => import('./views/AboutUs'));
const MyProfile          = lazy(() => import('./views/MyProfile'));
const BookingHistory     = lazy(() => import('./views/BookingHistory'));
const Dashboard          = lazy(() => import('./views/Dashboard'));
const CarTypes           = lazy(() => import('./views/CarTypes'));
const AddBookings        = lazy(() => import('./views/AddBookings'));
const Promos             = lazy(() => import('./views/Promos'));
const Users              = lazy(() => import('./views/Users'));
const CustomerDetails    = lazy(() => import('./views/CustomerDetails'));
const Notifications      = lazy(() => import('./views/Notifications'));
const Settings           = lazy(() => import('./views/Settings'));
const Complain           = lazy(() => import('./views/Complain'));
const AddMoney           = lazy(() => import('./views/AddMoney'));
const Withdraws          = lazy(() => import('./views/Withdraws'));
const WalletTransfers    = lazy(() => import('./views/WalletTransfers'));
const AllReports         = lazy(() => import('./views/AllReports'));
const ContactUs          = lazy(() => import('./views/ContactUs'));
const UserWallet         = lazy(() => import('./views/UserWallet'));
const CarsList           = lazy(() => import('./views/CarsList'));
const TermCondition      = lazy(() => import('./views/TermCondition'));
const DriverContract     = lazy(() => import('./views/DriverContract'));
const DeleteAccount      = lazy(() => import('./views/DeleteAccount'));
const Sos                = lazy(() => import('./views/Sos'));
const DriverDetails      = lazy(() => import('./views/DriverDetails'));
const Error              = lazy(() => import('./views/Error'));
const BookingDetails     = lazy(() => import('./views/BookingDetails'));
const UserDocuments      = lazy(() => import('./views/UserDocuments'));
const AddNotifications   = lazy(() => import('./views/AddNotifications'));
const EditPromo          = lazy(() => import('./views/EditPromo'));
const EditCar            = lazy(() => import('./views/EditCar'));
const CarDetail          = lazy(() => import('./views/CarDetail'));
const Descubre           = lazy(() => import('./views/Descubre'));
const DescubreLugarScreen = lazy(() => import('./views/DescubreLugarScreen'));
const Negocios           = lazy(() => import('./views/Negocios'));
const NegociosRegistro   = lazy(() => import('./views/NegociosRegistro'));
const EditCarType        = lazy(() => import('./views/EditCarType'));
const EditUser           = lazy(() => import('./views/EditUser'));
const PaymentSettings    = lazy(() => import('./views/PaymentSettings'));
const Companies          = lazy(() => import('./views/Companies'));
const CompanyDetail      = lazy(() => import('./views/CompanyDetail'));
const CompaniesDashboard = lazy(() => import('./views/CompaniesDashboard'));
const LugaresAdmin       = lazy(() => import('./views/LugaresAdmin'));
const IAScreen           = lazy(() => import('./views/IAScreen'));
const CarsDashboard      = lazy(() => import('./views/CarsDashboard'));
const UsersDashboard     = lazy(() => import('./views/UsersDashboard'));
const BookingsDashboard  = lazy(() => import('./views/BookingsDashboard'));
const ReportsDashboard   = lazy(() => import('./views/ReportsDashboard'));
const SupportChat        = lazy(() => import('./views/SupportChat'));

const libraries = GoogleMapApiOptions.libraries;

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

i18n
  .use(initReactI18next)
  .init({
    resources: {},
    fallbackLng: "en",
    ns: ["translations"],
    defaultNS: "translations",
    interpolation: { escapeValue: false },
  });

const PageLoader = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#F6F7FB' }}>
    <CircularProgress sx={{ color: '#8E4585' }} />
  </Box>
);

function App() {
  useJsApiLoader({
    id: 'google-map',
    googleMapsApiKey: GoogleMapApiConfig,
    version: GoogleMapApiOptions.v,
    libraries,
  });

  const theme = createTheme();

  return (
    <HelmetProvider>
      <Provider store={store}>
        <FirebaseProvider config={FirebaseConfig}>
          <GoogleOAuthProvider clientId={webClientId}>
            <ThemeProvider theme={theme}>
              <AuthLoading>
                <BrowserRouter future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
                  <ScrollToTop />
                  <Suspense fallback={<PageLoader />}>
                    <Routes>
                      <Route path="/dashboard"    element={<ProtectedRoute permit={"admin,fleetadmin"}><Dashboard /></ProtectedRoute>} />
                      <Route path="/bookings"     element={<ProtectedRoute permit={"customer,admin,driver,fleetadmin"}><BookingHistory /></ProtectedRoute>} />
                      <Route path="/bookings/bookingdetails/:id" element={<ProtectedRoute permit={"customer,admin,driver,fleetadmin"}><BookingDetails /></ProtectedRoute>} />
                      <Route path="/profile"      element={<ProtectedRoute permit={"customer,admin,driver,fleetadmin"}><MyProfile /></ProtectedRoute>} />
                      <Route path="/cartypes"     element={<ProtectedRoute permit={"admin"}><CarTypes /></ProtectedRoute>} />
                      <Route path="/cars"         element={<ProtectedRoute permit={"admin,fleetadmin,driver"}><CarsList /></ProtectedRoute>} />
                      <Route path="/addbookings"  element={<ProtectedRoute permit={"admin,fleetadmin,customer"}><AddBookings /></ProtectedRoute>} />
                      <Route path="/promos"       element={<ProtectedRoute permit={"admin"}><Promos /></ProtectedRoute>} />
                      <Route path="/users/:id"    element={<ProtectedRoute permit={"admin,fleetadmin"}><Users /></ProtectedRoute>} />
                      <Route path="/users/customerdetails/:id" element={<ProtectedRoute permit={"admin,fleetadmin"}><CustomerDetails /></ProtectedRoute>} />
                      <Route path="/users/driverdetails/:id"   element={<ProtectedRoute permit={"admin,fleetadmin"}><DriverDetails /></ProtectedRoute>} />
                      <Route path="/users/userdocuments/:rId/:id" element={<ProtectedRoute permit={"admin,fleetadmin"}><UserDocuments /></ProtectedRoute>} />
                      <Route path="/notifications"   element={<ProtectedRoute permit={"admin"}><Notifications /></ProtectedRoute>} />
                      <Route path="/addtowallet"     element={<ProtectedRoute permit={"admin"}><AddMoney /></ProtectedRoute>} />
                      <Route path="/userwallet"      element={<ProtectedRoute permit={"customer,driver,fleetadmin"}><UserWallet /></ProtectedRoute>} />
                      <Route path="/withdraws"          element={<ProtectedRoute permit={"admin"}><Withdraws /></ProtectedRoute>} />
                      <Route path="/wallettransfers"    element={<ProtectedRoute permit={"admin"}><WalletTransfers /></ProtectedRoute>} />
                      <Route path="/sos"             element={<ProtectedRoute permit={"admin"}><Sos /></ProtectedRoute>} />
                      <Route path="/complain"        element={<ProtectedRoute permit={"admin"}><Complain /></ProtectedRoute>} />
                      <Route path="/support"         element={<ProtectedRoute permit={"admin"}><SupportChat /></ProtectedRoute>} />
                      <Route path="/allreports"      element={<ProtectedRoute permit={"admin,fleetadmin"}><AllReports /></ProtectedRoute>} />
                      <Route path="/settings"        element={<ProtectedRoute permit={"admin"}><Settings /></ProtectedRoute>} />
                      <Route path="/paymentsettings" element={<ProtectedRoute permit={"admin"}><PaymentSettings /></ProtectedRoute>} />
                      <Route path="/lugares"         element={<ProtectedRoute permit={"admin,fleetadmin"}><LugaresAdmin /></ProtectedRoute>} />
                      <Route path="/ia"              element={<ProtectedRoute permit={"admin"}><IAScreen /></ProtectedRoute>} />
                      <Route path="/companies"       element={<ProtectedRoute permit={"admin,fleetadmin"}><Companies /></ProtectedRoute>} />
                      <Route path="/companies/dashboard" element={<ProtectedRoute permit={"admin,fleetadmin"}><CompaniesDashboard /></ProtectedRoute>} />
                      <Route path="/companies/:id"   element={<ProtectedRoute permit={"admin,fleetadmin"}><CompanyDetail /></ProtectedRoute>} />
                      <Route path="/cars/dashboard"  element={<ProtectedRoute permit={"admin,fleetadmin"}><CarsDashboard /></ProtectedRoute>} />
                      <Route path="/users/dashboard" element={<ProtectedRoute permit={"admin,fleetadmin"}><UsersDashboard /></ProtectedRoute>} />
                      <Route path="/bookings/dashboard" element={<ProtectedRoute permit={"admin,fleetadmin,driver,customer"}><BookingsDashboard /></ProtectedRoute>} />
                      <Route path="/allreports/dashboard" element={<ProtectedRoute permit={"admin,fleetadmin"}><ReportsDashboard /></ProtectedRoute>} />
                      <Route path="/descubre"        element={<Descubre />} />
                      <Route path="/descubre/lugar"  element={<DescubreLugarScreen />} />
                      <Route path="/negocios"          element={<Negocios />} />
                      <Route path="/negocios/registro" element={<NegociosRegistro />} />
                      <Route path="/contact-us"      element={<ContactUs />} />
                      <Route path="/about-us"        element={<AboutUs />} />
                      <Route path="/privacy-policy"  element={<PrivacyPolicy />} />
                      <Route path="/term-condition"  element={<TermCondition />} />
                      <Route path="/driver-contract" element={<DriverContract />} />
                      <Route path="/delete-account"  element={<DeleteAccount />} />
                      <Route path="/login"           element={<LoginPage />} />
                      <Route path="/users/edituser/:usertype"     element={<ProtectedRoute permit={"admin,fleetadmin"}><EditUser /></ProtectedRoute>} />
                      <Route path="/users/edituser/:usertype/:id" element={<ProtectedRoute permit={"admin,fleetadmin"}><EditUser /></ProtectedRoute>} />
                      <Route path="/notifications/addnotifications" element={<ProtectedRoute permit={"admin"}><AddNotifications /></ProtectedRoute>} />
                      <Route path="/cars/detail/:id"  element={<ProtectedRoute permit={"admin,fleetadmin"}><CarDetail /></ProtectedRoute>} />
                      <Route path="/cars/editcar"     element={<ProtectedRoute permit={"admin,fleetadmin,driver"}><EditCar /></ProtectedRoute>} />
                      <Route path="/cars/editcar/:id" element={<ProtectedRoute permit={"admin,fleetadmin,driver"}><EditCar /></ProtectedRoute>} />
                      <Route path="/promos/editpromo"     element={<ProtectedRoute permit={"admin"}><EditPromo /></ProtectedRoute>} />
                      <Route path="/promos/editpromo/:id" element={<ProtectedRoute permit={"admin"}><EditPromo /></ProtectedRoute>} />
                      <Route path="/cartypes/editcartype"     element={<ProtectedRoute permit={"admin,fleetadmin,driver"}><EditCarType /></ProtectedRoute>} />
                      <Route path="/cartypes/editcartype/:id" element={<ProtectedRoute permit={"admin,fleetadmin,driver"}><EditCarType /></ProtectedRoute>} />
                      <Route path="/*" element={<Error />} />
                      <Route path="/"  element={<LandingPage />} />
                    </Routes>
                  </Suspense>
                </BrowserRouter>
              </AuthLoading>
            </ThemeProvider>
          </GoogleOAuthProvider>
        </FirebaseProvider>
      </Provider>
    </HelmetProvider>
  );
}

export default App;
